import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Header } from 'semantic-ui-react';
import { getCompanyDealExclusions } from '~/api/dealExclusions';
import { Table } from '~/components';

type Props = {
  companyId: string;
};

function formatWhen(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch {
    return iso;
  }
}

export default function CompanyDealExclusionHistory({ companyId }: Props) {
  const { data, isLoading, isError } = useQuery(
    ['admin-deal-exclusions-company', companyId],
    () => getCompanyDealExclusions(companyId),
    { enabled: !!companyId }
  );

  const rows = data?.exclusions ?? [];

  return (
    <div style={{ marginTop: '32px' }}>
      <Header as="h3" style={{ marginBottom: '8px' }}>
        Deal exclusion history
      </Header>
      <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
        Deals this company is or was excluded from (admin-only). Reasons are stored per exclusion.
      </p>

      {isLoading ? (
        <p>Loading exclusion history…</p>
      ) : isError ? (
        <p style={{ color: '#a00' }}>Could not load exclusion history.</p>
      ) : rows.length === 0 ? (
        <p style={{ color: '#666' }}>No deal exclusions recorded for this company.</p>
      ) : (
        <div className="summary-table-container">
          <Table compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell color="grey">Deal</Table.HeaderCell>
                <Table.HeaderCell color="grey">Type</Table.HeaderCell>
                <Table.HeaderCell color="grey">Reason</Table.HeaderCell>
                <Table.HeaderCell color="grey">Last updated</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row: any) => {
                const isPortfolio = Boolean(row.portfolioId ?? row.portfolio?.id);
                const dealName =
                  (isPortfolio ? row.portfolio?.name : row.project?.name) || '—';
                const dealId = isPortfolio ? row.portfolio?.id : row.project?.id;
                const href = isPortfolio
                  ? `/portfolio/${dealId}/bidding`
                  : `/project/${dealId}/bidding`;

                return (
                  <Table.Row key={row.id}>
                    <Table.Cell>
                      {dealId ? (
                        <Link to={href}>{dealName}</Link>
                      ) : (
                        dealName
                      )}
                    </Table.Cell>
                    <Table.Cell>{isPortfolio ? 'Portfolio' : 'Project'}</Table.Cell>
                    <Table.Cell>{row.reason?.trim() ? row.reason : '—'}</Table.Cell>
                    <Table.Cell>{formatWhen(row.updatedAt)}</Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
}
